import React, { useContext, useEffect, useState, useTransition } from "react";
import { AuthContext } from "../context/AuthContext";
import { useQuery, useQueryClient, useQueries } from "@tanstack/react-query";
import axios from "axios";
import { startOfDay, endOfDay, parseISO, isBefore, isValid, subDays } from "date-fns";
import HomeLine from "../components/HomeLine.jsx";
import NumberCard from "../components/Card/NumberCard.jsx"
import Card from "../components/Card/Card";
import Loader from "../components/Loader";
import { getRoamingComputers, getTopThreat, getTraficBlocked, getDashboardSubscription, getDashboardContractsCisco, getDashboardIncidentsGraph, getViolations, getDashboardSecureEndpoint, getDashboardSentinelOne, getThreats, getAllLicencesPaloAlto, getDashboardIncidentsListGraph } from "../scripts/api_dashboard";
import { useTranslation } from "react-i18next";

export default function Home() {
  const { apiUrl, user, entrepriseRoles } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const today = endOfDay(new Date(), 23);
  const week = startOfDay(subDays(today, 7));
  const date = [week, today];
  const [umbrellaData, setUmbrellaData] = useState([]);
  const [securonixData, setSecuronixData] = useState([]);
  const [licencesPalo, setLicencesPalo] = useState([]);
  const [sentinelOneData, setSentinelOneData] = useState([]);
  const [incidentsData, setIncidentsData] = useState();
  const [allEquipements, setAllEquipements] = useState({});
  const [secureEndpointData, setSecureEndpointData] = useState({});
  const [percentageEquipement, setPercentageEquipement] = useState([]);
  const [percentageWebex, setPercentageWebex] = useState([]);
  const [loadingBilan, setLoadingBilan] = useState(true);
  const { t } = useTranslation();
  const [incidentsTitle, setIncidentsTitle] = useState(t("Tickets des 12 derniers mois"));
  const hasEquipementRole = entrepriseRoles?.includes("ROLE_EQUIPEMENT");
  const hasCertificatsRole = entrepriseRoles?.includes("ROLE_CERTIFICATS");
  const hasWebexRole = entrepriseRoles?.includes("ROLE_WEBEX");
  const hasPaloRole = entrepriseRoles?.includes("ROLE_PALO");
  const hasSubscriptionRole = entrepriseRoles?.includes("ROLE_SUBSCRIPTION");
  const hasContractRole = entrepriseRoles?.includes("ROLE_CONTRACT");

  useEffect(() => {
  }, [user, queryClient]);

  const getAllEquipements = async () => {
    const res = await axios(`${apiUrl}equipement/index`, {
      method: "GET",
      withCredentials: true,
    });

    return res.data;
  };

  // Get data for table
  const getCertificatList = async () => {
    const res = await axios(`${apiUrl}certificats`, {
      method: "GET",
      withCredentials: true,
    });

    if (user.roles.includes("ROLE_SUPERADMIN")) {
      return res.data;
    } else {
      return res.data.filter((incident) => incident.is_active === "1");
    }
  };

  // get all devices and stock in devices array
  async function getDevices() {
    // define an empty array
    const allDevices = await axios.get(`${apiUrl}webex/devices_live`, {
      withCredentials: true,
    });

    if (allDevices.data.errors) {
      return [];
    }

    return allDevices.data.items;
  }

  // Request to get all equipements palo
  const getAllPalo = async () => {
    const response = await axios.get(`${apiUrl}eqptpalo/`, {
      method: "GET",
      withCredentials: true,
    });

    return response.data;
  };

  // Request to get all licences and set a new array
  const getAllLicences = async () => {
    try {
      const responses = await axios.all([
        axios.get(`${apiUrl}license/palo/`, {
          method: "GET",
          withCredentials: true,
        }),
        axios.get(`${apiUrl}eqptpalo/`, {
          method: "GET",
          withCredentials: true,
        }),
      ]);

      // set array
      const licences = responses[0].data;
      const eqpt = responses[1].data;

      // set an empty array to stock famille object
      let family = [];
      eqpt.forEach((equipement) => {
        family.push(equipement.famille);
      });

      // create an array by set the equipement id in key and famille who matched
      const familiesById = eqpt.reduce((result, equipement) => {
        result[equipement.id] = equipement.famille;
        return result;
      }, {});

      // add famille in licences by match palo.id
      const licenceFamily = licences.map((licence) => {
        const famille = familiesById[licence.palo_id];
        return { ...licence, famille };
      });

      return licenceFamily;
    } catch (error) {
    }
  };

  const roamingDashboard = useQuery({
    queryKey: ["roamingDashboard"],
    queryFn: () => getRoamingComputers(apiUrl),
    enabled: entrepriseRoles?.includes("ROLE_UMBRELLA")
  });

  const threatDashboard = useQuery({
    queryKey: ["threatDashboard"],
    queryFn: () => getTopThreat(apiUrl),
    enabled: entrepriseRoles?.includes("ROLE_UMBRELLA")
  });

  const violationsDashboard = useQuery({
    queryKey: ["violationsDashboard"],
    queryFn: () => getViolations(apiUrl, date),
    enabled: entrepriseRoles?.includes("ROLE_SECURONIX")
  });

  const threatsDashboard = useQuery({
    queryKey: ["threatsDashboard"],
    queryFn: () => getThreats(apiUrl, date),
    enabled: entrepriseRoles?.includes("ROLE_SECURONIX")
  });

  const licencesPaloAltoDashboard = useQuery({
    queryKey: ["licencesPaloAltoDashboard"],
    queryFn: () => getAllLicencesPaloAlto(apiUrl),
    enabled: entrepriseRoles?.includes("ROLE_PALO")
  });

  const dashboardSentinelOne = useQuery({
    queryKey: ["dashboardSentinelOne"],
    queryFn: () => getDashboardSentinelOne(apiUrl),
    enabled: entrepriseRoles?.includes("ROLE_SENTINELONE")
  });

  const dashboardIncidents = useQuery({
    queryKey: ["dashboardIncidents"],
    queryFn: () => getDashboardIncidentsGraph(apiUrl),
    enabled: entrepriseRoles?.includes("ROLE_INCIDENT")
  });

  const dashboardIncidentsList = useQuery({
    queryKey: ["dashboardIncidentsList"],
    queryFn: () => getDashboardIncidentsListGraph(apiUrl),
    enabled: entrepriseRoles?.includes("ROLE_INCIDENT")
  });

  const dashboardSecureEndpoint = useQuery({
    queryKey: ["dashboardSecureEndpoint"],
    queryFn: () => getDashboardSecureEndpoint(apiUrl),
    enabled: entrepriseRoles?.includes("ROLE_AMP")
  })

  const dashboardSubscription = useQuery({
    queryKey: ["dashboardSubscription"],
    queryFn: () => getDashboardSubscription(apiUrl),
    enabled: entrepriseRoles?.includes("ROLE_SUBSCRIPTION")
  })

  const dashboardContractsCisco = useQuery({
    queryKey: ["dashboardContractsCisco"],
    queryFn: () => getDashboardContractsCisco(apiUrl),
    enabled: entrepriseRoles?.includes("ROLE_CONTRACT")
  })

  const traficDashboard = useQuery({
    queryKey: ["traficDashboard"],
    queryFn: () => getTraficBlocked(apiUrl),
    enabled: entrepriseRoles?.includes("ROLE_UMBRELLA")
  });

  useEffect(() => {
    if (!entrepriseRoles?.includes("ROLE_UMBRELLA")) {
      setUmbrellaData([{ text: "Data not found" }]);
    }
    if (!entrepriseRoles?.includes("ROLE_SECURONIX")) {
      setSecuronixData([{ text: "Data not found" }]);
    }
    if (!entrepriseRoles?.includes("ROLE_PALO")) {
      setLicencesPalo([{ text: "Data not found" }]);
    }
    if (!entrepriseRoles?.includes("ROLE_SENTINELONE")) {
      setSentinelOneData([{ text: "Data not found" }]);
    }
    if (!entrepriseRoles?.includes("ROLE_INCIDENT")) {
      setIncidentsData([{ text: "Data not found" }]);
    }
    if (!entrepriseRoles?.includes("ROLE_AMP")) {
      setSecureEndpointData([{ text: "Data not found" }]);
    }
  }, [entrepriseRoles]);

  useEffect(() => {
    if (roamingDashboard.isFetched && roamingDashboard.data && threatDashboard.isFetched && traficDashboard.isFetched) {
      let umbrellaArray;

      if (roamingDashboard.data.length === 0 && !threatDashboard.data && !traficDashboard.data) {
        umbrellaArray = [
          {
            text: "Data not found"
          }
        ];
      } else {
        const onlineClientsPercentage = roamingDashboard.data.length > 0
          ? Math.round((roamingDashboard.data.filter((item) => item.status !== "Off").length * 100) / roamingDashboard.data.length) + "%"
          : "0%";

        umbrellaArray = [
          {
            text: "Client en ligne",
            number: onlineClientsPercentage,
            color: "green",
          },
          {
            text: "Nombre de menaces",
            color: threatDashboard.data && threatDashboard.data.length > 0 ? "red" : "green",
            number: threatDashboard.data && threatDashboard.data.length > 0 ? threatDashboard.data.length : "0"
          },
          {
            text: "Trafic bloqué",
            color: traficDashboard.data && traficDashboard.data.length > 0 ? "red" : "green",
            number: traficDashboard.data && traficDashboard.data.length > 0 ? traficDashboard.data.length : "0"
          }
        ];
      }
      setUmbrellaData(umbrellaArray);
    }
  }, [roamingDashboard.data, roamingDashboard.isFetched, threatDashboard.data, threatDashboard.isFetched, traficDashboard.data, traficDashboard.isFetched]);

  useEffect(() => {
    if (violationsDashboard.isFetched && threatsDashboard.isFetched) {
      let securonixArray;

      if (!violationsDashboard.data || !threatsDashboard.data) {
        securonixArray = [
          {
            text: "Data not found"
          }
        ];
      } else {
        securonixArray = [
          {
            text: "Nombre de menaces",
            number: threatsDashboard.data ? threatsDashboard.data.length : 0,
            color: threatsDashboard.data && threatsDashboard.data.length > 0 ? "red" : "green",
          },
          {
            text: "Nombre de violations",
            number: violationsDashboard.data ? violationsDashboard.data.length : 0,
            color: violationsDashboard.data && violationsDashboard.data.length > 0 ? "red" : "green",
          },
        ];
      }
      setSecuronixData(securonixArray);
    }
  }, [violationsDashboard.data, violationsDashboard.isFetched, threatsDashboard.data, threatsDashboard.isFetched]);

  useEffect(() => {
    if (licencesPaloAltoDashboard.isFetched) {
      const today = endOfDay(new Date());

      const licences = Array.isArray(licencesPaloAltoDashboard.data?.licences)
        ? licencesPaloAltoDashboard.data.licences
        : [];

      const expiredLicences = licences.filter((licence) => {
        if (!licence?.date_fin || typeof licence.date_fin !== "string") {
          return false;
        }

        const dateFin = parseISO(licence.date_fin);
        if (!isValid(dateFin)) {
          return false;
        }

        return isBefore(dateFin, today);
      });

      const validLicences = licences.length - expiredLicences.length;

      let licencesPaloArray;

      if (licences[0]?.data === "No license palo found") {
        licencesPaloArray = [
          {
            text: "Data not found"
          }
        ];
      } else {
        licencesPaloArray = [
          {
            text: t("Nombre de pare-feus"),
            number: (licencesPaloAltoDashboard.data?.eqptFirewall?.length || 0) + (licencesPaloAltoDashboard.data?.eqpt?.length || 0),
            color: "normal",
          },
          {
            text: t("Licences à jour"),
            number: validLicences,
            color: validLicences > 0 ? "green" : "red",
          },
          {
            text: t("Licences expirées"),
            number: expiredLicences.length,
            color: expiredLicences.length > 0 ? "red" : "green",
          },
        ];
      }
      setLicencesPalo(licencesPaloArray);
    }
  }, [licencesPaloAltoDashboard.data, licencesPaloAltoDashboard.isFetched]);

  useEffect(() => {
    if (dashboardSentinelOne.isFetched) {
      if (Array.isArray(dashboardSentinelOne.data) && dashboardSentinelOne.data.length === 0) {
        setSentinelOneData([{ text: "Data not found" }]);
      } else if (dashboardSentinelOne.data && !Array.isArray(dashboardSentinelOne.data)) {
        const resolved = dashboardSentinelOne.data.threats.resolved;
        const notResolved = dashboardSentinelOne.data.threats.notResolved;
        const notMitigated = dashboardSentinelOne.data.threats.notMitigated;
        const suspiciousNotResolved = dashboardSentinelOne.data.threats.suspiciousNotResolved;
        const filteredData = [{
          [t("menaces résolues")]: resolved,
          [t("menaces non résolues")]: notResolved,
          [t("menaces non atténuées")]: notMitigated,
          [t("menaces suspectes et non résolues")]: suspiciousNotResolved
        }]
        setSentinelOneData(filteredData);
      } else {
        setSentinelOneData([
          {
            text: "Data not found"
          }
        ]);
      }
    }
  }, [dashboardSentinelOne.data, dashboardSentinelOne.isFetched]);

  useEffect(() => {
    if (dashboardSecureEndpoint.isFetched) {
      if (dashboardSecureEndpoint.data) {
        if (dashboardSecureEndpoint.data.devices.length === 0 || dashboardSecureEndpoint.data === "No valid network data available") {
          setSecureEndpointData([
            {
              text: "Data not found"
            }
          ]);
          return;
        } else {
          const protect = Math.round((dashboardSecureEndpoint.data.devices.filter((item) => item.policy.name === "Protect").length));
          const audits = Math.round((dashboardSecureEndpoint.data.devices.filter((item) => item.policy.name === "Audit").length));

          const filteredData = [{
            [t("protégés")]: protect,
            "audits": audits
          }]

          setSecureEndpointData(filteredData);
        }
      } else {
        setSecureEndpointData([
          {
            text: "Data not found"
          }
        ]);
      }
    }
  }, [dashboardSecureEndpoint.data, dashboardSecureEndpoint.isFetched]);

  useEffect(() => {
    if (dashboardIncidents.isFetched) {
      if (dashboardIncidents.data) {
        if (dashboardIncidents.data.graph_count_ticket[0] === "No data") {
          setIncidentsData([
            {
              text: "Data not found"
            }
          ]);
        } else {
          let filteredData = dashboardIncidents.data.graph_count_ticket.slice(-12);

          if (filteredData.length === 1) {
            const ticketMonthString = filteredData[0].month;
            const [year, month] = ticketMonthString.split('-');
            const targetYear = parseInt(ticketMonthString.split('-')[0], 10);
            const targetMonth = parseInt(ticketMonthString.split('-')[1], 10);

            const fullMonthNames = {
              "01": t("Janvier"), "02": t("Février"), "03": t("Mars"), "04": t("Avril"),
              "05": t("Mai"), "06": t("Juin"), "07": t("Juillet"), "08": t("Août"),
              "09": t("Septembre"), "10": t("Octobre"), "11": t("Novembre"), "12": t("Décembre")
            };

            const monthName = fullMonthNames[month] || ticketMonthString;

            setIncidentsTitle(`${t("Tickets des 12 derniers jours de")} ${monthName} ${year}`);

            const getDayString = (date) => {
              const y = date.getFullYear();
              const m = (date.getMonth() + 1).toString().padStart(2, '0');
              const d = date.getDate().toString().padStart(2, '0');
              return `${y}-${m}-${d}`;
            };

            const allTicketsInMonth = dashboardIncidentsList.data.filter(ticket => {
              if (!ticket.createdon) return false;
              const ticketDate = new Date(ticket.createdon);
              return ticketDate.getFullYear() === targetYear && (ticketDate.getMonth() + 1) === targetMonth;
            });

            allTicketsInMonth.sort((a, b) => new Date(b.createdon) - new Date(a.createdon));

            const last12Tickets = allTicketsInMonth.slice(0, 12);

            const dailyCounts = {};

            for (const ticket of last12Tickets) {
              const ticketDate = new Date(ticket.createdon);
              const dayString = getDayString(ticketDate);
              dailyCounts[dayString] = (dailyCounts[dayString] || 0) + 1;
            }

            filteredData = Object.keys(dailyCounts).map(day => {
              return {
                "ticket_count": dailyCounts[day].toString(),
                "day": day
              };
            });

            filteredData.sort((a, b) => a.day.localeCompare(b.day));
          }
          setIncidentsData(filteredData);
        }
      }
    }
  }, [dashboardIncidents.data, dashboardIncidents.isFetched]);

  const data = useQueries({
    queries: [
      hasEquipementRole
        ? { queryKey: ["equipements"], queryFn: getAllEquipements }
        : { queryKey: ["equipements"], queryFn: async () => null, enabled: false, initialData: null },
      hasCertificatsRole
        ? { queryKey: ["certificats"], queryFn: getCertificatList }
        : { queryKey: ["certificats"], queryFn: async () => null, enabled: false, initialData: null },
      hasWebexRole
        ? { queryKey: ["webex"], queryFn: getDevices }
        : { queryKey: ["webex"], queryFn: async () => null, enabled: false, initialData: null },
      hasPaloRole
        ? { queryKey: ["palo"], queryFn: getAllPalo }
        : { queryKey: ["palo"], queryFn: async () => null, enabled: false, initialData: null },
      hasPaloRole
        ? { queryKey: ["paloLicence"], queryFn: getAllLicences }
        : { queryKey: ["paloLicence"], queryFn: async () => null, enabled: false, initialData: null },
    ],
  });

  // array for each request
  const equipements = data[0];
  const certificats = data[1];
  const webex = data[2];
  const paloLicence = data[3];

  useEffect(() => {
    setLoadingBilan(true);
    setAllEquipements({});
  }, [apiUrl, entrepriseRoles]);

  useEffect(() => {
    const isBilanLoading =
      (hasEquipementRole && (equipements.isLoading || equipements.isFetching || !equipements.isFetched)) ||
      (hasCertificatsRole && (certificats.isLoading || certificats.isFetching || !certificats.isFetched)) ||
      (hasPaloRole && (paloLicence.isLoading || paloLicence.isFetching || !paloLicence.isFetched)) ||
      (hasContractRole && (dashboardContractsCisco.isLoading || dashboardContractsCisco.isFetching || !dashboardContractsCisco.isFetched)) ||
      (hasSubscriptionRole && (dashboardSubscription.isLoading || dashboardSubscription.isFetching || !dashboardSubscription.isFetched));

    if (isBilanLoading) {
      setLoadingBilan(true);
      return;
    }

    if (!hasEquipementRole) {
      setAllEquipements("service_no_active");
      setLoadingBilan(false);
      return;
    }

    if (equipements.isFetched && equipements.data) {
      const allequipements = {
        reseau: equipements.data.filter((item) => {
          return item.famille ? item.famille.nom !== "Visio" && item.famille.nom !== "FIREWALL" : item;
        }),
        visio: equipements.data.filter((item) => {
          return item.famille && item.famille.nom === "Visio";
        }),
        certificats: hasCertificatsRole && certificats.isFetched ? certificats.data : [],
        contractsCisco: hasContractRole && dashboardContractsCisco.isFetched ? dashboardContractsCisco.data : [],
        licencesFirewall: hasPaloRole && paloLicence.isFetched ? paloLicence.data : [],
        subscription: hasSubscriptionRole && dashboardSubscription.isFetched ? dashboardSubscription.data : [],
      };

      setAllEquipements(allequipements);
      setLoadingBilan(false);
    }

  }, [
    hasEquipementRole,
    hasCertificatsRole,
    hasPaloRole,
    hasContractRole,
    hasSubscriptionRole,
    equipements.data,
    equipements.isLoading,
    equipements.isFetching,
    equipements.isFetched,
    certificats.data,
    certificats.isLoading,
    certificats.isFetching,
    certificats.isFetched,
    paloLicence.data,
    paloLicence.isLoading,
    paloLicence.isFetching,
    paloLicence.isFetched,
    dashboardContractsCisco.data,
    dashboardContractsCisco.isLoading,
    dashboardContractsCisco.isFetching,
    dashboardContractsCisco.isFetched,
    dashboardSubscription.data,
    dashboardSubscription.isLoading,
    dashboardSubscription.isFetching,
    dashboardSubscription.isFetched
  ])

  useEffect(() => {
    if (allEquipements.reseau) {
      const percentageEquipement = [
        {
          percentage:
            allEquipements &&
            allEquipements.reseau.filter(
              (equipement) =>
                equipement.famille &&
                ["WLC", "AP"].includes(equipement.famille.nom)
            ).length,
          color: "#124188",
          value: "Wireless",
        },
        {
          percentage:
            allEquipements &&
            allEquipements.reseau.filter(
              (equipement) =>
                equipement.famille && ["SWITCH"].includes(equipement.famille.nom)
            ).length,
          color: "#997adb",
          value: "LAN",
        },
        {
          percentage:
            allEquipements &&
            allEquipements.reseau.filter(
              (equipement) =>
                !equipement.famille || equipement.famille &&
                !["WLC", "AP", "SWITCH"].includes(equipement.famille.nom)
            ).length || 0,
          color: "#AEC353",
          value: "Autres",
        },
      ];
      setPercentageEquipement(percentageEquipement);
    }

    const percentageWebex = [
      {
        percentage:
          webex.data &&
          webex.data.filter(
            (device) =>
              device.connectionStatus === "connected"
          ).length,
        color: "#86CB92",
        value: "Connected",
      },
      {
        percentage:
          webex.data &&
          webex.data.filter(
            (device) =>
              device.connectionStatus === "connected_with_issues"
          ).length,
        color: "#F18805",
        value: "Issues",
      },
      {
        percentage:
          webex.data &&
          webex.data.filter(
            (device) =>
              device.connectionStatus === "disconnected"
          ).length,
        color: "#DF2935",
        value: "Disconnected",
      },
      {
        percentage:
          webex.data &&
          webex.data.filter(
            (device) =>
              device.connectionStatus === "unknown"
          ).length,
        color: "#E9E9EC",
        value: "Unknown",
      },
      {
        percentage:
          webex.data &&
          webex.data.filter(
            (device) =>
              device.connectionStatus === "offline_expired"
          ).length,
        color: "grey",
        value: "Offline expired",
      },
      {
        percentage:
          webex.data &&
          webex.data.filter((device) =>
            !device.connectionStatus ||
            device.connectionStatus &&
            !["connected", "connected_with_issues", "disconnected", "unknown", "offline_expired"].includes(device.connectionStatus)
          ).length,
        color: "#8977B0",
        value: "Autres",
      },
    ];

    setPercentageWebex(percentageWebex);
  }, [webex.data, allEquipements]);

  return (
    <section className="page-home">
      <div className="page-home-flex">
        <div className="page-home-grid-wrapper">
          <div className="page-home-grid-wrapper-row">
            <Card
              lien={"/umbrella"}
              logo={"../assets/cisco_logo.png"}
              title={"Umbrella"}
              infos={t("Les informations affichées couvrent une période de 7 jours")}
              data={
                entrepriseRoles?.includes("ROLE_UMBRELLA")
                  ? !roamingDashboard.isLoading && !threatDashboard.isLoading && !traficDashboard.isLoading
                    ? umbrellaData
                    : "Loading"
                  : umbrellaData
              }
            />
            <Card
              lien={"/securonix"}
              logo={"../assets/securonix.png"}
              title={"Securonix"}
              infos={t("Les informations affichées couvrent une période de 7 jours")}
              data={
                entrepriseRoles?.includes("ROLE_SECURONIX")
                  ? !violationsDashboard.isLoading && !threatsDashboard.isLoading
                    ? securonixData
                    : "Loading"
                  : securonixData
              }
            />
            <Card
              lien={"/firewall/palo"}
              logo={"../assets/palo_logo.png"}
              title={"Palo Alto"}
              data={
                entrepriseRoles?.includes("ROLE_PALO")
                  ? !licencesPaloAltoDashboard.isLoading
                    ? licencesPalo
                    : "Loading"
                  : licencesPalo
              }
            />
          </div>
          <div className="page-home-grid-wrapper-row large">
            <Card
              lien={"/sentinelOne"}
              logo={"../assets/sentinelone.png"}
              title={"SentinelOne"}
              infos={t("Les informations affichées sont en temps réel")}
              data={
                entrepriseRoles?.includes("ROLE_SENTINELONE")
                  ? !dashboardSentinelOne.isLoading
                    ? sentinelOneData
                    : "Loading"
                  : sentinelOneData
              }
              graphDough={true}
              colors={["#0D99E6", "#7517F8", "#07BEAA", "#FFC246"]}
            />
            <Card
              lien={"/tickets"}
              title={incidentsTitle}
              infos={t("Les informations affichées sont en temps réel")}
              data={
                entrepriseRoles?.includes("ROLE_INCIDENT")
                  ? !dashboardIncidents.isLoading && dashboardIncidents.data
                    ? incidentsData
                    : "Loading"
                  : incidentsData
              }
              graphLine={true}
            />
          </div>
          <div className="page-home-grid-wrapper-row">
            <Card lien={"/secure_endpoint/events"}
              logo={"../assets/cisco_logo.png"}
              title={"Secure Endpoint"}
              infos={t("Les informations affichées sont en temps réel")}
              data={
                entrepriseRoles?.includes("ROLE_AMP")
                  ? !dashboardSecureEndpoint.isLoading
                    ? secureEndpointData
                    : "Loading"
                  : secureEndpointData
              }
              graphDough={true}
              colors={["#997ADB", "#6100FF"]}
            />
            {hasEquipementRole && (equipements.isLoading || (equipements.isFetching && !equipements.isFetched)) ? (
              <div className="homeline" style={{ border: "none" }}>
                <Loader />
              </div>
            ) : allEquipements.reseau && allEquipements.reseau.length > 0 ? (
              <HomeLine data={allEquipements.reseau} percentage={percentageEquipement} type={"equipements/cisco"} />
            ) : (
              <div className="homeline" style={{ border: "none" }}>
                <h2>0 {t("équipements disponibles")}</h2>
                <div className="homeline-item_bar">
                  <div
                    className="homeline-item_bar-container"
                    style={{
                      width: (0.1 * 100) / 100 + "%",
                      backgroundColor: "black",
                    }}
                  >
                  </div>
                </div>
              </div>
            )}
            {hasWebexRole && (webex.isLoading || (webex.isFetching && !webex.isFetched)) ? (
              <div className="homeline" style={{ border: "none" }}>
                <Loader />
              </div>
            ) : !webex.isError && webex.data && webex.data.length > 0 ? (
              <>
                <HomeLine data={webex.data} percentage={percentageWebex} type={"webex"} />
              </>
            ) : (
              <div className="homeline" style={{ border: "none" }}>
                <h2>0 {t("équipements collaboration disponible")}</h2>
                <div className="homeline-item_bar">
                  <div
                    className="homeline-item_bar-container"
                    style={{
                      width: (0.1 * 100) / 100 + "%",
                      backgroundColor: "black",
                    }}
                  >
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="page-home-card-wrapper">
          <Card type={'bilan'} infos={t("Voici votre bilan globale de vos différents équipements, certificats, abonnements et contrats")} bilanData={allEquipements} loadingBilan={loadingBilan} />
        </div>
      </div>
    </section>
  );
}

